import { Text } from "@chakra-ui/react";
import { HightouchCdpDiagram } from "components/animations/hightouch-cdp-diagram";
import { HightouchCdpFeaturesAccordion } from "components/content";
import { sectionSpacingY } from "components/section";
import { useCdpIndustry } from "contexts/cdp-industry-context";
import React from "react";
import Balancer from "react-wrap-balancer";
import { SolidPlate } from "ui/plates";
import { ProductFeatures } from "ui/product-features";
import { TextTitle } from "ui/text";
import { integrationsCount } from "../../constants/integrations";
import mobileDiagram1 from "../../../public/assets/cdp-industries/mobile-diagram-1.png";
import mobileDiagram2 from "../../../public/assets/cdp-industries/mobile-diagram-2.png";
import mobileDiagram3 from "../../../public/assets/cdp-industries/mobile-diagram-3.png";

export function CdpIndustryWhyHightouch() {
  const { industry } = useCdpIndustry();

  return (
    <SolidPlate theme="dark" bgColor={`new.${industry.color}.950`} spotlightColor={`new.${industry.color}.900`}>
      <TextTitle
        theme="dark"
        size="lg"
        align="center"
        title={<Balancer>Why Hightouch for {industry.abbreviatedName ?? industry.name}?</Balancer>}
        description={`Your CDP vendor should mold to your data — not the other way around. Hightouch is purpose-built to handle the complexity of ${industry.name}.`}
        mb={{ base: "biggest", md: "huge" }}
        maxW={industry.whyHightouchTitleMaxW ?? "2xl"}
      />

      <ProductFeatures
        theme="dark"
        media={<HightouchCdpDiagram />}
        productFeatures={[
          {
            id: "data",
            title: (
              <>
                Leverage any data point{" "}
                <Text as="span" fontWeight="normal">
                  in your warehouse – not just users, accounts, and events.
                </Text>
              </>
            ),
            mobileImage: {
              src: mobileDiagram1,
              alt: "Abstract illustration of data getting sent from a data warehouse.",
            },
          },
          {
            id: "audiences",
            title: (
              <>
                Build and activate audiences{" "}
                <Text as="span" fontWeight="normal">
                  directly from your warehouse.
                </Text>
              </>
            ),
            mobileImage: {
              src: mobileDiagram2,
              alt: "Abstract illustration of warehouse data getting used for Data Activation, Audience Management, and AI Decisioning.",
            },
          },
          {
            id: "infrastructure",
            title: (
              <>
                Integrate with your existing data infrastructure{" "}
                <Text as="span" fontWeight="normal">
                  on your warehouse.
                </Text>
              </>
            ),
            mobileImage: {
              src: mobileDiagram3,
              alt: `Abstract illustration of data getting activated into ${integrationsCount}+ tools, such as Google Ads, Salesforce, Braze, and Iterable.`,
            },
          },
        ]}
      />

      <HightouchCdpFeaturesAccordion
        title={<Balancer>A complete CDP for {industry.name}</Balancer>}
        mt={sectionSpacingY}
        mb="big"
        insightsAnimationProps={industry.audienceOverlap}
      />
    </SolidPlate>
  );
}
